import React, { useRef, useEffect, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useLang } from '../context/LanguageContext';

const t = {
  fr: {
    label: 'Derrière Cocoonut Digital',
    intro: 'Derrière Cocoonut Digital, il y a Rham — un regard créatif, curieux et profondément inspiré par les lieux où l\'on partage une table.',
    p1: 'Originaire des Philippines et aujourd\'hui installée en Alsace, elle a toujours été attirée par les univers où l\'on crée des expériences : restaurants, hôtels, événements et lieux de vie. Pour elle, chaque établissement est comme une recette : un équilibre subtil entre atmosphère, saveurs, accueil et histoire à raconter.',
    p2: 'Avant de créer Cocoonut Digital, Rham a exploré plusieurs horizons — entre restauration, hospitalité, création visuelle et projets créatifs — avec toujours la même idée en tête : mettre en lumière les lieux qui ont une âme et raconter leur histoire avec sensibilité.',
    p3: 'Aujourd\'hui, elle accompagne restaurants, hôtels et lieux de vie dans leur communication digitale pour traduire leur univers en images, en émotions et en histoires qui donnent envie de pousser la porte.',
    backstage: 'Et côté coulisses ?',
    fun1: '🎤 Elle aime chanter et créer',
    fun2: '🌍 Voyager et découvrir de nouvelles cuisines',
    fun3: '🍜 Et bien sûr, partager de bons repas',
    closing: 'Cocoonut Digital, c\'est simplement ça : un regard sensible pour raconter l\'âme des lieux.',
  },
  en: {
    label: 'Behind Cocoonut Digital',
    intro: 'Behind Cocoonut Digital, there\'s Rham — a creative eye, curious and deeply inspired by places where people share a table.',
    p1: 'Originally from the Philippines and now based in Alsace, she has always been drawn to worlds where experiences are created: restaurants, hotels, events and hospitality venues. For her, every establishment is like a recipe: a subtle balance of atmosphere, flavours, hospitality and a story to tell.',
    p2: 'Before creating Cocoonut Digital, Rham explored many paths — between catering, hospitality, visual creation and creative projects — always with the same idea in mind: shining a light on places with a soul and telling their story with sensitivity.',
    p3: 'Today, she supports restaurants, hotels and hospitality venues with their digital communication, translating their world into images, emotions and stories that make people want to walk through the door.',
    backstage: 'Behind the scenes?',
    fun1: '🎤 She loves singing and creating',
    fun2: '🌍 Travelling and discovering new cuisines',
    fun3: '🍜 And of course, sharing great meals',
    closing: 'Cocoonut Digital is simply that: a sensitive eye for telling the story of places with a soul.',
  },
};

const BehindSection = () => {
  const { lang } = useLang();
  const tx = t[lang];
  const sectionRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start end", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], isMobile ? [0, 0] : [-20, 20]);
  const imageOpacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], isMobile ? [1, 1, 1, 1] : [0.85, 1, 1, 0.85]);

  return (
    <section ref={sectionRef} className="relative py-20 md:py-28 bg-white overflow-hidden" data-testid="behind-section">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-12 md:gap-16">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="w-full md:w-2/5 flex-shrink-0">
              <div className="relative">
                <motion.div className="aspect-[3/4] overflow-hidden rounded-lg shadow-lg" style={{ y: imageY, opacity: imageOpacity }}>
                  <img src="/assets/images/behind-cocoonut.jpg" alt={tx.label} className="w-full h-full object-cover" data-testid="behind-image" />
                </motion.div>
                <div className="absolute -bottom-4 -right-4 w-24 h-24 border border-[#d4a574]/30 rounded-lg -z-10" />
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.2 }} className="flex-1 text-center md:text-left" data-testid="behind-text-content">
              <span className="text-sm uppercase tracking-widest text-[#8b9faa] font-mono mb-6 block">{tx.label}</span>
              <div className="space-y-4 text-[#151515]/75 leading-relaxed">
                <p className="text-lg md:text-xl text-[#1a3a52] font-light leading-relaxed">{tx.intro}</p>
                <p className="text-base">{tx.p1}</p>
                <p className="text-base">{tx.p2}</p>
                <p className="text-base">{tx.p3}</p>
                <div className="pt-4">
                  <p className="text-base font-medium text-[#1a3a52] mb-3">{tx.backstage}</p>
                  <div className="space-y-1 text-base">
                    <p>{tx.fun1}</p>
                    <p>{tx.fun2}</p>
                    <p>{tx.fun3}</p>
                  </div>
                </div>
                <p className="text-base italic text-[#4a5d6c] pt-2">{tx.closing}</p>
              </div>
              <motion.div initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.6 }} className="w-16 h-px bg-[#d4a574] mt-8 md:mx-0 mx-auto origin-left" />
            </motion.div>
          </div>
        </div>
      </div>
      <div className="absolute top-0 right-0 w-64 h-64 bg-[#f5e6d3]/30 rounded-full blur-3xl pointer-events-none" />
    </section>
  );
};

export default BehindSection;
